import React, { useState } from "react";
import { toast } from "react-toastify";
import Search from "../../components/form/Search";
import Button from "../../components/form/Button";
import Table from "../../components/elements/table/Table";
import { setTitle } from "../../helpers/MetaTag";
import { handleFilterChange } from "../../helpers";
import DeleteModal from "../../components/common/DeleteModal";

import useFetch from "../../hooks/useFetch";
import { useDebounce } from "../../hooks/useDebounce";
import LimitSelect from "../../components/elements/LimitSelect";
import {
  userDelete,
  userList,
  userStatusChange,
} from "../../services/userService";
import ManageUserModal from "../../components/sections/admin/user/modals/ManageUserModal";
import useMutation from "../../hooks/useMutation";
import Switch from "../../components/form/Switch";
import Badge from "../../components/elements/Badge";
import { classNames } from "../../helpers/classNames";
import dayjs from "dayjs";
import { resetPasswordRequest } from "../../services/authService";
import { useSelector } from "react-redux";
import ManageAccessControlModal from "../../components/sections/admin/user/modals/ManageAccessControlModal";
import CsvPdfDownload from "../../components/elements/CsvPdfDownload";
import { useGetPermission } from "../../hooks/useGetPermission";

const Users = () => {
  setTitle("Vpeters Admin | user");
  const { permission, status } = useGetPermission("users");
  const { session, loginData } = useSelector((state) => state?.authProvider);

  const [selectedItems, setSelectedItems] = useState([]);
  const [modalOpen, setModalOpen] = useState({ isOpen: false, data: null });
  const [keyword, setKeyword] = useState("");
  const search = useDebounce(keyword, 300);
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    data: null,
  });
  const [filters, setFilters] = useState({
    limit: 10,
    page: 1,
    sortQuery: "-lastLogin",
  });
  const [accessModal, setAccessModal] = useState({ isOpen: false, data: null });

  const { data, error, isLoading, updateData, isFetching, refetch } = useFetch({
    dependencies: ["user", filters, search, permission?.list],
    fetchFunction: async () => {
      const { roleCode = [], statusCode = [], ...restFilters } = filters || {};

      const queryParams = {
        ...restFilters,
        ...(search && { keyword: search }),
        ...Object.fromEntries(
          [...roleCode].map((val, i) => [`roleCode[${i}]`, val])
        ),
        ...Object.fromEntries(
          [...statusCode].map((val, i) => [`statusCode[${i}]`, val])
        ),
      };

      const res = await userList(queryParams);
      if (res?.status !== 200) {
        toast.warning(res?.message);
      }
      return res?.docs || [];
    },
    enabled: !!permission?.list && status === "ok",
  });

  //   user status change handler =================>
  const { mutate: statusChangeHandler, isPending: statusChangeInPending } =
    useMutation({
      mutationFn: async ({ id, status }) => {
        const res = await userStatusChange({
          ids: id,
          status,
        });
        return { res, payload: { id, status } };
      },
      onSuccess: ({ res, payload }) => {
        if (res?.status !== 200) {
          updateData((pre) => {
            pre.data = (pre?.data || []).map((elm) =>
              elm?._id === payload?.id
                ? { ...elm, status: payload?.status === 1 ? 2 : 1 }
                : elm
            );
            return { ...pre };
          });
          toast.warning(res?.message || res?.error);
        }
      },
      onError: (error) => {
        console.log(error);
      },
    });

  const handleResetPassword = async (userData) => {
    try {
      const body = {
        emails: [userData?.email],
      };
      const resp = await resetPasswordRequest({ ...body });
      if (resp.status === 200) {
        toast.success(resp.message);
      } else {
        toast.warn(resp.message);
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const tableHeadData = [
    {
      _id: 1,
      name: "User Name",
      align: "left",
    },
    {
      _id: 2,
      name: "Email",
      align: "left",
    },
    {
      _id: 3,
      name: "Role",
      align: "left",
      isFilter: true,
      isFilterSearch: false,
      onFilter: (val) => {
        if (val?.length > 0) {
          setFilters((pre) => ({
            ...pre,
            roleCode: val?.map((item) => item?.value),
            page: 1,
          }));
        } else {
          setFilters((pre) => {
            delete pre?.roleCode;
            return { ...pre };
          });
        }
      },
      filterData: [
        { name: "Applicant", value: "user", type: "checkbox" },
        { name: "Donor", value: "donor", type: "checkbox" },
        { name: "Admin", value: "admin", type: "checkbox" },
      ],
    },
    {
      _id: 4,
      name: "Last Login",
      align: "left",
    },
    {
      _id: 5,
      name: "# of Awards",
      align: "left",
    },
    {
      _id: 6,
      name: "# of Rejections",
      align: "left",
    },
    {
      _id: "loginMethod",
      name: "Login Method",
      align: "left",
    },
    {
      _id: "status",
      name: "Status",
      align: "left",
      isFilter: true,
      isFilterSearch: false,
      onFilter: (val) => {
        if (val?.length > 0) {
          setFilters((pre) => ({
            ...pre,
            statusCode: val?.map((item) => item?.value),
            page: 1,
          }));
        } else {
          setFilters((pre) => {
            delete pre?.statusCode;
            return { ...pre };
          });
        }
      },
      filterData: [
        { name: "Active", value: 1, type: "checkbox" },
        { name: "Inactive", value: 2, type: "checkbox" },
      ],
    },
    {
      _id: 7,
      name: "Action",
      align: "left",
    },
  ];

  const loginMethod = [
    { method: "basic", label: "System" },
    { method: "google", label: "Gmail" },
    { method: "fb", label: "Facebook" },
    { method: "apple", label: "Apple" },
  ];

  // Table Body Data =====================>
  const tableData = data?.data?.map((item) => {
    return {
      _id: item?._id,
      rowData: [
        {
          _id: 1,
          type: "user",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: classNames(item?.firstName, item?.lastName) || "-",
          image: item?.image?.url,
        },
        {
          _id: 2,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: item?.email || "-",
        },
        {
          _id: 3,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: item?.roleCode || "-",
        },
        {
          _id: 4,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: item?.lastLogin
            ? dayjs(item?.lastLogin).format("DD MMM, YYYY")
            : "-",
        },
        {
          _id: 4,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: "-",
        },
        {
          _id: 5,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: "-",
        },
        {
          _id: "loginMethod",
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: item?.mainLogin
            ? loginMethod?.find((elm) => elm?.method === item?.mainLogin)?.label
            : "-",
        },
        {
          _id: "status",
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: (
            <div className="flex gap-2 items-center">
              <Switch
                className={
                  "!w-10 !h-5 before:!w-4 before:!h-4 peer-checked:before:!left-[22px]"
                }
                checked={item?.status === 1}
                onChange={() => {
                  if (permission?.changeStatus && status === "ok") {
                    updateData((pre) => {
                      pre.data = (pre?.data || []).map((elm) =>
                        elm?._id === item?._id
                          ? { ...elm, status: elm?.status === 1 ? 2 : 1 }
                          : elm
                      );
                      pre.metadata = {
                        ...pre.metadata,
                        extraCount: {
                          ...pre.metadata.extraCount,
                          inActiveUser:
                            item?.status === 1
                              ? pre.metadata.extraCount.inActiveUser + 1
                              : pre.metadata.extraCount.inActiveUser - 1,
                        },
                      };
                      return { ...pre };
                    });
                    statusChangeHandler({
                      id: item?._id,
                      status: item?.status === 1 ? 2 : 1,
                    });
                  }
                }}
                disabled={!permission?.changeStatus}
              />
              <Badge
                type={item?.status === 1 ? "success" : "danger"}
                label={item?.status === 1 ? "Active" : "Inactive"}
              />
            </div>
          ),
        },
        {
          _id: 6,
          type: "action",
          align: "left",
          cellClass: "w-[5%]",
          actionData: [
            // {
            //   _id: 0,
            //   name: (
            //     <div
            //       className={classNames(
            //         item?.status === 1
            //           ? "text-green-600/80"
            //           : "text-rose-600/80"
            //       )}
            //     >
            //       {item?.status === 1 ? "Active" : "Inactive"}
            //     </div>
            //   ),
            //   icon: "",
            //   onClick: () => {
            //     if (permission?.changeStatus && status === "ok") {
            //       updateData((pre) => {
            //         pre.data = (pre?.data || []).map((elm) =>
            //           elm?._id === item?._id
            //             ? { ...elm, status: elm?.status === 1 ? 2 : 1 }
            //             : elm
            //         );
            //         return { ...pre };
            //       });
            //       statusChangeHandler({
            //         id: item?._id,
            //         status: item?.status === 1 ? 2 : 1,
            //       });
            //     }
            //   },
            //   standout: false,
            // },
            {
              _id: 1,
              name: "Reset Passwoord",
              icon: "fa-regular fa-key",
              onClick: () => {
                handleResetPassword(item);
              },
              standout: false,
            },
            ...(permission?.edit && status === "ok"
              ? [
                  {
                    _id: 2,
                    name: "Edit",
                    icon: "fa-regular fa-pen-to-square",
                    onClick: () => {
                      setModalOpen({ isOpen: true, data: item });
                    },
                    standout: false,
                  },
                ]
              : []),
            ...(item?.roleCode === "admin"
              ? [
                  {
                    _id: "accessControl",
                    name: "Access Control",
                    icon: "fa-regular fa-universal-access",
                    standout: false,
                    onClick: () => {
                      setAccessModal({ isOpen: true, data: item });
                    },
                  },
                ]
              : []),
            ...(permission?.delete && status === "ok"
              ? [
                  {
                    _id: 3,
                    name: "Delete",
                    icon: "fa-regular fa-trash-can",
                    standout: true,
                    onClick: () => {
                      setDeleteModal({
                        isOpen: true,
                        data: item,
                      });
                    },
                  },
                ]
              : []),
          ],
        },
      ],
    };
  });

  // add or edit user =================>
  function userManageResponse(res) {
    if (res?.status === 200 || res?.status === 201) {
      if (res?.status === 201) {
        if (filters?.page === 1) {
          updateData((pre) => ({
            ...pre,
            data: [res?.data, ...(pre?.data || [])],
          }));
          refetch();
        } else {
          setFilters((pre) => ({ ...pre, page: 1 }));
        }
      }
      if (res?.status === 200) {
        updateData((pre) => ({
          ...pre,
          data: pre?.data?.map((item) =>
            item?._id === res?.data?._id ? res?.data : item
          ),
        }));
      }
      setModalOpen({ isOpen: false, data: null });
    } else {
      toast.warning(res?.message || res?.error);
    }
  }

  //   user delete handler =================>
  const { mutate: deleteHandler, isPending: deletePending } = useMutation({
    mutationFn: async (payload) => {
      const res = await userDelete({
        ids: Array.isArray(payload) ? payload : [payload],
      });
      return { res, payload: Array.isArray(payload) ? payload : [payload] };
    },
    onSuccess: ({ res, payload }) => {
      if (res?.status === 200) {
        setDeleteModal({ isOpen: false, data: null });
        updateData((pre) => {
          pre.data = (pre?.data || [])?.filter(
            (item) => !payload?.includes(item?._id)
          );
          return { ...pre };
        });
        if (data?.metadata?.totalDocs > filters?.limit) {
          refetch();
        }
      } else {
        toast.warning(res?.message || res?.error);
      }
    },
    onError: (error) => {
      console.log(error);
    },
  });

  return (
    <div className="space-y-4 w-full flex flex-col pb-10">
      <div className="space-y-8">
        <div className="font-bold text-center text-lg xl:text-3xl text-chestnut-rose">
          User Management
        </div>
        <div className="flex items-center gap-3">
          <div className="w-36">
            <LimitSelect
              value={filters?.limit}
              allValue={data?.metadata?.totalDocs}
              onChange={(val) => handleFilterChange("limit", val, setFilters)}
            />
          </div>
          <div className="w-72">
            <Search
              placeholder={"Search here"}
              search={(val) => {
                setKeyword(val);
                handleFilterChange("page", 1, setFilters);
              }}
            />
          </div>
          {permission?.add && status === "ok" && (
            <div className="relative">
              <Button
                buttonClasses={
                  "!bg-transparent border border-transparent !h-10 hover:!bg-chestnut-rose/10 hover:!border-chestnut-rose/10"
                }
                buttonLabel={"Create New"}
                buttonLabelClasses={"font-medium !text-sm text-chestnut-rose"}
                buttonFunction={() =>
                  setModalOpen({ isOpen: true, data: null })
                }
              />
            </div>
          )}
          <CsvPdfDownload
            showPdf={false}
            csvData={data?.data?.map((item) => ({
              Name: `${item?.firstName} ${item?.lastName}`,
              Email: item?.email,
              Image: item?.image?.url,
              Role: item?.roleCode,
              "Email Verified": item?.emailVerified === 1 ? "Yes" : "No",
              "Login Method": item?.mainLogin,
              "Active User": item?.isActive ? "Yes" : "No",
              "Last Login": dayjs(item?.lastLogin).format("DD MMM, YYYY"),
              "Created On": dayjs(item?.createdAt).format("DD MMM, YYYY"),
            }))}
            filename="user-list"
            buttonLabel={"Download CSV"}
          />
          <div className="ml-auto space-x-3">
            <span className="text-chestnut-rose font-CanelaDeck">
              Total : {data?.metadata?.totalDocs || 0}
            </span>
            <span className="text-chestnut-rose font-CanelaDeck">
              Inactive : {data?.metadata?.extraCount?.inActiveUser || 0}
            </span>
          </div>
        </div>
      </div>
      {status === "ok" &&
        (permission?.list ? (
          <Table
            tableHeadData={tableHeadData}
            tableData={tableData}
            isLoder={isLoading}
            pageCount={data?.metadata?.totalPages}
            currentPage={filters?.page}
            onPageChange={(val) => {
              handleFilterChange("page", val, setFilters);
              setSelectedItems([]);
            }}
            handleOrdering={(val) => {
              console.log(val);
            }}
            isFetching={isFetching}
            isOrderingDisabled={true}
            selectedItems={selectedItems}
            setSelectedItems={setSelectedItems}
            tableActionDropdownContainer={"!w-48"}
          />
        ) : (
          <div className="font-semibold text-xl text-center h-60 flex items-center justify-center text-shades-07">
            You don't have permission to view this list.
          </div>
        ))}
      <ManageUserModal
        open={modalOpen?.isOpen}
        data={modalOpen?.data}
        close={() => setModalOpen({ isOpen: false, data: null })}
        getResponse={userManageResponse}
      />
      <ManageAccessControlModal
        open={accessModal?.isOpen}
        data={accessModal?.data}
        close={() => setAccessModal({ isOpen: false, data: null })}
        getResponse={(val) => {
          console.log(val);
        }}
      />
      <DeleteModal
        open={deleteModal?.isOpen}
        loading={deletePending}
        onClose={() => setDeleteModal((pre) => ({ data: null, isOpen: false }))}
        deleteHandler={() => {
          deleteHandler(deleteModal?.data?._id);
        }}
      />
    </div>
  );
};

export default Users;
